"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PROOF, SITE } from "../../data/site";
import HeroField from "../layout/HeroField";

const RISE = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

export default function Hero() {
  const reduce = useReducedMotion();
  const t = (delay: number) =>
    reduce
      ? { duration: 0 }
      : { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <section id="top" className="relative overflow-hidden">
      {/* backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="grid-field mask-fade-b absolute inset-0" />
        <HeroField />
        <div
          className="glow-drift absolute left-1/2 top-[-18rem] h-[36rem] w-[70rem] rounded-full opacity-90 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgb(var(--accent) / 0.16), transparent 70%)",
          }}
        />
      </div>

      <div className="shell flex min-h-[86vh] flex-col justify-center pb-16 pt-32 md:min-h-[88vh] md:pb-24 md:pt-40">
        <motion.p
          className="eyebrow"
          initial="hidden"
          animate="show"
          variants={RISE}
          transition={t(0)}
        >
          {SITE.location} · {SITE.role}
        </motion.p>

        <Headline text={SITE.headline} reduce={!!reduce} />

        <motion.p
          className="prose-body mt-7 max-w-2xl text-[1.0625rem] md:text-lg"
          initial="hidden"
          animate="show"
          variants={RISE}
          transition={t(0.34)}
        >
          {SITE.intro}
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center gap-2.5"
          initial="hidden"
          animate="show"
          variants={RISE}
          transition={t(0.46)}
        >
          <a href="#work" className="btn btn-primary">
            See the work
            <Arrow />
          </a>
          <a
            href={SITE.links.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
          >
            Resume
          </a>
          <a href="#contact" className="btn">
            Get in touch
          </a>
        </motion.div>

        {/* proof strip */}
        <motion.dl
          className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))] md:mt-20 md:grid-cols-4"
          initial="hidden"
          animate="show"
          variants={RISE}
          transition={t(0.52)}
        >
          {PROOF.map((p, i) => (
            <div key={p.label} className="bg-bg px-4 py-5">
              <dt className="font-mono text-2xl tracking-tight text-ink md:text-[1.75rem]">
                <Settle value={p.value} delay={560 + i * 70} />
              </dt>
              <dd className="mt-1.5 text-sm text-ink">{p.label}</dd>
              <dd className="mt-0.5 text-2xs text-faint">{p.note}</dd>
            </div>
          ))}
        </motion.dl>
      </div>
    </section>
  );
}

/**
 * Word-by-word reveal from behind a clip.
 *
 * Each word gets its own overflow box so it rises out of nothing rather than
 * fading in. The vertical padding and matching negative margin keep the box
 * off the descenders, which the clip would otherwise shave.
 */
function Headline({ text, reduce }: { text: string; reduce: boolean }) {
  const words = React.useMemo(() => text.split(" "), [text]);

  return (
    <motion.h1
      className="display mt-6 max-w-4xl text-balance text-[clamp(2.5rem,7vw,5rem)]"
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: {
          transition: reduce
            ? { duration: 0 }
            : { staggerChildren: 0.034, delayChildren: 0.05 },
        },
      }}
    >
      {words.map((word, i) => (
        <React.Fragment key={`${word}-${i}`}>
          <span className="-mb-[0.16em] inline-block overflow-hidden pb-[0.16em] align-bottom">
            <motion.span
              className="inline-block"
              variants={{
                hidden: { y: reduce ? 0 : "108%" },
                show: {
                  y: 0,
                  transition: reduce
                    ? { duration: 0 }
                    : { duration: 0.78, ease: [0.19, 1, 0.22, 1] },
                },
              }}
            >
              {word}
            </motion.span>
          </span>{" "}
        </React.Fragment>
      ))}
    </motion.h1>
  );
}

/**
 * Counts a proof figure up to its final value once the tile is on screen.
 *
 * Reads the shape out of the authored string rather than taking a number and
 * a format, so `10,000+` and `16.67` both come back looking the way they were
 * written. Anything it cannot parse is rendered as-is.
 */
function Settle({ value, delay }: { value: string; delay: number }) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const reduce = useReducedMotion();

  const parsed = React.useMemo(() => {
    const m = value.match(/^(\D*)([\d,]+(?:\.\d+)?)(.*)$/);
    if (!m) return null;
    const [, prefix, core, suffix] = m;
    const target = Number(core.replace(/,/g, ""));
    if (!Number.isFinite(target)) return null;
    const dot = core.indexOf(".");
    const decimals = dot === -1 ? 0 : core.length - dot - 1;
    const grouped = core.includes(",");
    const format = (n: number) => {
      const fixed = n.toFixed(decimals);
      const body = grouped
        ? Number(fixed).toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : fixed;
      return `${prefix}${body}${suffix}`;
    };
    return { target, format };
  }, [value]);

  const [shown, setShown] = React.useState(value);

  // Drop to zero before the first paint, otherwise the final figure flashes
  // for a frame and then appears to jump backwards.
  React.useLayoutEffect(() => {
    if (parsed && !reduce) setShown(parsed.format(0));
  }, [parsed, reduce]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !parsed || reduce) return;

    let raf = 0;
    let timer = 0;
    let start = 0;

    const run = (now: number) => {
      if (!start) start = now;
      // Short and sharply eased: a measurement settling, not a slot machine.
      const p = Math.min(1, (now - start) / 900);
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setShown(parsed.format(parsed.target * eased));
      if (p < 1) raf = requestAnimationFrame(run);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        timer = window.setTimeout(() => {
          raf = requestAnimationFrame(run);
        }, delay);
      },
      { threshold: 0.4 },
    );
    io.observe(el);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [parsed, delay, reduce]);

  return (
    <span ref={ref} className="tabular-nums">
      {parsed && !reduce ? shown : value}
    </span>
  );
}

function Arrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
